import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, AlertTriangle } from 'lucide-react';
import { deleteCategoriaProducto } from '../../services/categoriaProductoService';

interface DeleteProductsCategoryProps {
    isOpen: boolean;
    onClose: () => void;
    categoryId: number | null;
    categoryName?: string;
    onDeleted: (id: number) => void;
}

const C = {
    primary: 'var(--ad-primary, #3564db)',
    primaryDark: 'var(--ad-primary-dark, #8f1f64)',
    secondary: 'var(--ad-secondary, #3564db)',
    secondaryDark: '#004d7a',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accentDark: '#ea8406',
};

const css = `

.dpm-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes dpmFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes dpmSlideUp{from{opacity:0;transform:translateY(20px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.dpm-overlay{animation:dpmFadeIn .22s ease both;}
.dpm-dialog{animation:dpmSlideUp .3s cubic-bezier(.16,1,.3,1) both;}
.dpm-btn-danger{display:inline-flex;align-items:center;gap:6px;padding:10px 22px;border-radius:11px;border:none;background:linear-gradient(135deg,${C.primary},${C.primaryDark});color:#fff;font-weight:800;font-size:.88rem;cursor:pointer;box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.28);transition:all .22s;flex:1;justify-content:center;}
.dpm-btn-danger:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.34);}
.dpm-btn-danger:disabled{background:var(--ad-line, #e2e8f0);color:var(--ad-muted, #94a3b8);box-shadow:none;cursor:not-allowed;}
.dpm-btn-ghost{display:inline-flex;align-items:center;gap:6px;padding:10px 18px;border-radius:11px;border:1.5px solid var(--ad-line, #e2e8f0);background:var(--ad-soft, #f8fafc);color:var(--ad-ink, #374151);font-weight:700;font-size:.88rem;cursor:pointer;transition:all .2s;flex:1;justify-content:center;}
.dpm-btn-ghost:hover{background:var(--ad-line, #f1f5f9);}
`;

const DeleteProductsCategory: React.FC<DeleteProductsCategoryProps> = ({
    isOpen,
    onClose,
    categoryName,
    categoryId,
    onDeleted,
}) => {
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async () => {
        if (categoryId === null) return;
        setIsDeleting(true);
        try {
            await deleteCategoriaProducto(categoryId);
            onDeleted(categoryId);
            onClose();
        } catch (e) {
            /* Browser diagnostics omitted. */
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen || categoryId === null) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true"
                className="dpm-modal dpm-overlay"
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 99999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                    background: 'rgba(15,23,42,.55)',
                    backdropFilter: 'blur(6px)',
                }}
            >
                <LumaAccessiblePanel data-luma-dialog="true"
                    className="dpm-dialog card custom-card vx-card"
                    onClick={e => e.stopPropagation()}
                    style={{
                        background: 'var(--ad-surface, #fff)',
                        borderRadius: '22px',
                        width: '100%',
                        maxWidth: '420px',
                        boxShadow: '0 32px 80px rgba(0,0,0,.2)',
                        border: '1.5px solid var(--ad-line, #f1f5f9)',
                        overflow: 'hidden',
                    }}
                >
                    {/* Barra top roja */}
                    <div
                        style={{
                            height: '4px',
                            background: `linear-gradient(90deg,${C.primary},${C.primaryDark},color-mix(in srgb, ${C.primary} 50.2%, transparent))`,
                        }}
                    />

                    {/* Header */}
                    <div
                        style={{
                            padding: '20px 24px',
                            borderBottom: '1.5px solid var(--ad-line, #f1f5f9)',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div
                                style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '12px',
                                    background: `color-mix(in srgb, ${C.primary} 7.06%, transparent)`,
                                    border: `1.5px solid color-mix(in srgb, ${C.primary} 13.33%, transparent)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }} className=""
                            >
                                <Trash2 size={18} color={C.primary} />
                            </div>
                            <div>
                                <LumaAccessibleTitle
                                    style={{
                                        fontSize: '1rem',
                                        fontWeight:'650',
                                        color: 'var(--ad-ink, #0f172a)',
                                        letterSpacing: '-.02em',
                                    }}
                                >
                                    Eliminar Categoría
                                </LumaAccessibleTitle>
                                <p style={{ fontSize: '.72rem', color: 'var(--ad-muted, #94a3b8)', marginTop: '1px' }}>
                                    Esta acción no se puede deshacer
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '9px',
                                border: '1.5px solid var(--ad-line, #f1f5f9)',
                                background: 'var(--ad-soft, #f8fafc)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                            }}
                            onMouseEnter={e =>
                                ((e.currentTarget as HTMLButtonElement).style.background = 'var(--ad-line, #f1f5f9)')
                            }
                            onMouseLeave={e =>
                                ((e.currentTarget as HTMLButtonElement).style.background = 'var(--ad-soft, #f8fafc)')
                            }
                        >
                            <X size={14} color="var(--ad-muted, #64748b)" />
                        </button>
                    </div>

                    {/* Body */}
                    <div
                        style={{
                            padding: '20px 24px 24px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                        }}
                    >
                        {/* Barra de 4 colores */}
                        <div style={{ display: 'flex', gap: '4px' }}>
                            {[C.primary, C.secondary, C.accentDark, C.secondaryLight].map(
                                (c, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            height: '2px',
                                            width: i === 0 ? '16px' : '5px',
                                            borderRadius: '2px',
                                            background: c,
                                            opacity: 0.4,
                                        }}
                                    />
                                )
                            )}
                        </div>

                        <p style={{ fontSize: '.9rem', color: 'var(--ad-muted, #475569)', lineHeight: 1.65 }}>
                            ¿Estás seguro de que deseas eliminar la categoría{' '}
                            <strong style={{ color:'var(--ad-link, #2854c5)' }}>"{categoryName}"</strong>?
                        </p>

                        {/* Card advertencia */}
                        <div
                            style={{
                                display: 'flex',
                                gap: '10px',
                                padding: '12px 14px',
                                background: `color-mix(in srgb, ${C.primary} 2.75%, transparent)`,
                                border: `1.5px solid color-mix(in srgb, ${C.primary} 9.41%, transparent)`,
                                borderRadius: '12px',
                            }} className="card custom-card vx-card"
                        >
                            <AlertTriangle
                                size={15}
                                color={C.primary}
                                style={{ flexShrink: 0, marginTop: '1px' }}
                            />
                            <p
                                style={{
                                    fontSize: '.78rem',
                                    color:'var(--ad-link, #2854c5)',
                                    fontWeight: '600',
                                    lineHeight: 1.55,
                                }}
                            >
                                Se eliminarán todos los productos asociados a esta categoría. Esta
                                acción es irreversible.
                            </p>
                        </div>

                        {/* Botones */}
                        <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                            <button
                                onClick={onClose}
                                disabled={isDeleting}
                                className="dpm-btn-ghost"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="dpm-btn-danger"
                            >
                                {isDeleting ? (
                                    <>
                                        <span
                                            style={{
                                                width: '13px',
                                                height: '13px',
                                                border: '2px solid rgba(255,255,255,.4)',
                                                borderTopColor: '#fff',
                                                borderRadius: '50%',
                                                animation: 'spin .7s linear infinite',
                                                display: 'inline-block',
                                            }}
                                        />{' '}
                                        Eliminando...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={14} /> Eliminar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>,
        document.body
    );
};

export default DeleteProductsCategory;
